/**
 * Tour Narration Scripts
 *
 * These scripts are read by TTS (browser fallback or ElevenLabs)
 * Writing style: Carl Sagan / Buckminster Fuller / Isaac Asimov inspired
 * - Precise yet poetic
 * - Sense of wonder and cosmic perspective
 * - Systems thinking
 * - Optimistic futurism
 *
 * Voice inspiration: Carl Sagan's warmth and wonder,
 * Bucky Fuller's systems thinking, Asimov's clarity
 */

export const narrationScripts = {
  // SEGMENT 0: Opening
  '00-intro-wide': `Welcome, fellow explorer of possible futures.

Before you lies not merely a visualization, but a cosmos of methodologies—
eighteen distinct approaches to understanding what comes next.

The future, you see, is not a place we're traveling to.
It's a landscape we're actively constructing,
one decision, one assumption, one paradigm at a time.

What you're witnessing is the architecture of foresight itself.`,

  // SEGMENT 1: Strategic Foresight Center
  '01-strategic-foresight': `This is Strategic Foresight.

Not fortune-telling. Not prediction.
But rather, a disciplined framework for expanding our peripheral vision
across the possibility space of what might emerge.

It's the difference between being surprised by the future,
and being prepared to shape it.

At its core, foresight asks three fundamental questions:
What is probable? What is possible? And what is preferable?`,

  // SEGMENT 2: Six Pillars Overview
  '02-six-pillars': `Surrounding the center, you see six pillars.

This framework was developed by Professor Sohail Inayatullah—
a Malaysian-Pakistani futurist who holds the UNESCO Chair in Futures Studies.

Born between cultures, Inayatullah built his career
bridging Eastern wisdom traditions and Western systems thinking.
He saw that futures work had become too narrow,
too dominated by quantitative forecasting.

The Six Pillars emerged from this frustration.
A complete cognitive architecture for engaging uncertainty—
from mapping our current assumptions,
to transforming our preferred futures into reality.

Each pillar represents a different way of knowing,
a different mode of consciousness.

Let's explore each one.`,

  // SEGMENT 3: Mapping
  '03-mapping': `The first pillar: Mapping the Future.

Before we can change the future, we must first understand
how we currently think about it.

What assumptions are we making?
What mental models guide our decisions?
What do we believe is inevitable, and what do we believe is impossible?

Mapping reveals the often invisible frameworks
that constrain our imagination.

It's the essential first step in any foresight practice.`,

  // SEGMENT 4: Futures Triangle
  '04-futures-triangle': `Consider the Futures Triangle, developed by Inayatullah himself.

It maps three forces that shape every future:

The Push of the Present—the momentum of current trends.
Deep drivers like demographic shifts, technological trajectories.

The Pull of the Future—our aspirations and visions.
What we hope to create, what we're actively building toward.

And the Weight of the Past—our mental models, our institutions,
the accumulated inertia of how things have always been done.

The future emerges at the intersection of these three forces.
Understanding their interplay is the beginning of agency.`,

  // SEGMENT 5: Anticipating
  '05-anticipating': `The second pillar: Anticipating Change.

If mapping shows us where we stand,
anticipating shows us what's approaching on the horizon.

This is the practice of peripheral vision—
detecting the weak signals, the emerging patterns,
the early tremors of transformation
before they become earthquakes.

The organizations that thrive aren't those that react fastest.
They're the ones who saw the change coming
when it was still just a whisper.`,

  // SEGMENT 6: Environmental Scanning
  '06-env-scanning': `Environmental Scanning is systematic vigilance.

Consider Motorola in the nineteen-eighties.
Their scanning team monitored Japanese electronics firms religiously.
When Sony and Panasonic shifted strategies,
Motorola's scanners caught it early. They adapted. They survived.

But a decade later, those same scanners missed the digital revolution
happening in Silicon Valley—right in their own backyard.
They were watching the wrong periphery.

The scanning process looks for trends, wild cards, and weak signals—
those faint indicators of possible futures that haven't crystallized yet.

Jim Dator, the legendary Hawaiian futurist, put it perfectly:
Any useful statement about the future should at first appear ridiculous.

The future always arrives from the periphery.
The question is: which periphery are you watching?`,

  // SEGMENT 7: Timing
  '07-timing': `The third pillar: Timing the Future.

Futures don't arrive randomly.
They emerge in patterns, rhythms, cycles.

Understanding macro-historical patterns—
the rise and fall of civilizations,
the punctuated equilibrium of technological revolutions,
the generational cycles that repeat across centuries—

This temporal awareness transforms foresight
from speculation into navigation.

We're not passive observers of time.
We're participants in its unfolding patterns.`,

  // SEGMENT 8: Deepening
  '08-deepening': `The fourth pillar: Deepening Understanding.

Now we descend below the surface.

Most futures work focuses on trends and data—the visible layer.
But beneath every trend lies a worldview.
Beneath every worldview lies a myth.

Deepening methods ask:
What assumptions make this future seem natural or inevitable?
What cultural narratives support it?
What deeper story is being told?

To change the future, we must change the story.`,

  // SEGMENT 9: Causal Layered Analysis
  '09-cla': `Causal Layered Analysis—CLA—is Inayatullah's most powerful tool.

Developed in the nineteen-nineties, it emerged from his frustration
with futures work that never questioned its own assumptions.

CLA examines any issue at four levels:

The Litany: headlines, surface events, reactive noise.

Social Causes: systemic factors, structures, economic and political forces.

Worldview: ideological frameworks, cultural assumptions,
the paradigms that make certain futures seem rational.

And finally, Myth and Metaphor: the deep unconscious,
the archetypes and narratives that shape civilization itself.

Most policy addresses only the litany—treating symptoms.
Structural reform addresses social causes.
Transformative change addresses the myth.

CLA doesn't just analyze problems.
It reveals the epistemological foundations that created them.
And in doing so, it opens pathways to futures
that were previously unthinkable.`,

  // SEGMENT 10: Creating Alternatives
  '10-creating': `The fifth pillar: Creating Alternatives.

Here, we step from analysis into imagination.

One future is a prediction.
Multiple futures are preparation.

The methods in this pillar generate diverse scenarios—
not to predict which will happen,
but to stretch our collective imagination
beyond the single expected path.

The future isn't singular.
It's plural, probabilistic, participatory.

By creating alternative futures, we expand the range
of what we're prepared to encounter.`,

  // SEGMENT 11: Scenario Planning
  '11-scenarios': `Scenario Planning emerged at RAND in the nineteen-fifties,
where Herman Kahn taught military strategists to "think the unthinkable"—
nuclear war scenarios that felt obscene to imagine, yet essential to prevent.

But the method was perfected at Royal Dutch Shell in the seventies
by Pierre Wack, a Frenchman who'd studied Zen meditation in Japan.

In nineteen seventy-two, Wack's team built scenarios
exploring what would happen if oil prices collapsed—or exploded.
Oil was two dollars a barrel. It had only ever risen slowly.
The scenarios seemed absurd.

But Wack's team imagined ten-dollar oil. Fifteen-dollar oil.

When the Arab oil embargo hit in nineteen seventy-three,
oil spiked to sixteen dollars overnight.

Every major oil company panicked. Except Shell.
They'd already mentally rehearsed that future.
While competitors scrambled, Shell calmly executed plans
they'd war-gamed months earlier.

Scenarios aren't predictions.
They're wind tunnels for testing strategies against multiple plausible futures.

The goal isn't accuracy. It's preparedness.`,

  // SEGMENT 12: Transforming
  '12-transforming': `The sixth pillar: Transforming the Future.

This is where foresight becomes action.

We've mapped our assumptions.
We've anticipated emerging change.
We've deepened our understanding.
We've imagined alternatives.

Now: How do we create the future we actually want?

Transformation isn't about predicting the future.
It's about committing to one—
and then building the pathway to reach it.`,

  // SEGMENT 13: Backcasting
  '13-backcasting': `Backcasting inverts the planning process.

Traditional forecasting asks: Given where we are, where will we end up?

Backcasting asks: Given where we want to be, how do we get there?

In two thousand and five, Sweden's government convened the Commission
on Oil Independence. Their question wasn't "When will oil run out?"
but rather "What does a thriving, fossil-free Sweden look like in twenty fifty?"

They imagined that future in vivid detail.
Then worked backward, year by year, identifying every decision,
every infrastructure change, every innovation required
to make that future real.

The sociologist Elise Boulding called this "remembering the future"—
making preferred futures so tangible, so detailed,
that they feel like memories we're working to fulfill.

It's how Sweden planned their energy transition.
It's how we designed the pathway to the moon in the nineteen-sixties.

Backcasting is how impossible futures become inevitable ones.`,

  // SEGMENT 14: Closing
  '14-closing': `You've now journeyed through the Six Pillars of Foresight.

But this isn't merely an intellectual framework.
It's a practice, a discipline, a way of engaging with radical uncertainty.

Buckminster Fuller once said:
"You never change things by fighting the existing reality.
To change something, build a new model that makes the existing model obsolete."

That's what these methodologies enable.

The future will arrive whether we think about it or not.
The question is: Will we be conscious participants in its creation,
or passive recipients of someone else's vision?

Every methodology you've seen here is a tool for agency—
for expanding what we believe is possible,
and for building what we believe is preferable.

The future is not something we enter.
The future is something we create.

Thank you for exploring with us.
Now, go forth and practice foresight.`,

  // QUICK TOUR SCRIPTS (abbreviated versions)
  'quick-intro': `Welcome to Strategic Foresight—
a disciplined approach to exploring alternative futures.
What you see before you are eighteen methodologies,
organized into six pillars.
Let's take a brief tour through this landscape of possibility.`,

  'quick-pillars': `The Six Pillars framework, developed by Sohail Inayatullah,
organizes foresight methods into six modes:
Mapping current assumptions, Anticipating change,
Timing patterns, Deepening understanding,
Creating alternatives, and Transforming futures.
Each pillar offers unique tools for navigating uncertainty.`,

  'quick-scenarios': `Scenario Planning is perhaps the most famous foresight method.
Developed at RAND and perfected at Shell,
it creates multiple plausible futures—
not to predict which will happen,
but to prepare for whichever does.
Organizations that practice scenario planning
don't just react to change. They're ready for it.`,

  'quick-close': `You've glimpsed the landscape of Strategic Foresight.
Each methodology here is a doorway to deeper exploration.
Click any node to learn more, access videos, read papers,
and discover how these tools can expand your own capacity
to shape the future.
The cosmos awaits your exploration.`
};

/**
 * Get narration script by segment ID
 */
export function getNarrationScript(segmentId) {
  return narrationScripts[segmentId] || '';
}

/**
 * Get all script IDs
 */
export function getAllScriptIds() {
  return Object.keys(narrationScripts);
}

export default narrationScripts;
